import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { ColDef, GridApi } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";

import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { ViewChip } from "@/pages/delays/report/components/filters/Filters";
import {
	EndFilter,
	StartFilter,
} from "@/pages/delays/shared/components/StartEndFilter";
import {
	GroupedNotification,
	groupNotificationData,
	RollshopNotificationData,
} from "@/pages/rollshop/models/RollshopNotification";
import { getRollshopNotifications } from "@/pages/rollshop/repositories/RollshopNotificationRepository";
import { routeRollshopNotifications } from "@/setup/routes/rollshop.routes";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { AccessTime, DateRange } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { Moment } from "moment";
import { Group, Panel } from "react-resizable-panels";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { NotificationDetails } from "./components/details/NotificationDetails";
import { ActionTypeFilter } from "./components/table/ActionTypeFilter";
import { TableRollshopNotifications } from "./components/table/TableRollshopNotifications";

const moment = getMomentTz();

type FilterMode = "shift" | "period";

const formatDateForUrl = (date: Moment): string => date.format("YYYY-MM-DD");
const parseDateFromUrl = (dateStr: string): Moment | null => {
	const parsed = moment(dateStr, "YYYY-MM-DD", true);
	return parsed.isValid() ? parsed : null;
};

export const useRollshopNotifications = ({
	shiftId,
	start,
	end,
	enabled,
}: {
	shiftId?: number | null;
	start?: Date | null;
	end?: Date | null;
	enabled: boolean;
}) => {
	return useQuery<RollshopNotificationData, Error>({
		queryKey: [
			"rollshopNotifications",
			shiftId,
			start?.getTime(),
			end?.getTime(),
		],
		queryFn: ({ signal }) =>
			getRollshopNotifications({ shiftId, start, end }, signal),
		refetchInterval: 60_000,
		enabled: enabled,
	});
};

export const RollshopNotificationPage = () => {
	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const notificationIdParam = searchParams.get("notificationId");

	const getInitialFilterMode = (): FilterMode => {
		const fromParam = searchParams.get("from");
		const toParam = searchParams.get("to");
		if (fromParam || toParam) return "period";
		return "shift";
	};

	const [filterMode, setFilterMode] =
		useState<FilterMode>(getInitialFilterMode);
	const [startDate, setStartDate] = useState<Moment | null>(() => {
		const fromParam = searchParams.get("from");
		if (fromParam) {
			const parsed = parseDateFromUrl(fromParam);
			return parsed || moment().clone().subtract(1, "day");
		}
		return moment().clone().subtract(1, "day");
	});
	const [endDate, setEndDate] = useState<Moment | null>(() => {
		const toParam = searchParams.get("to");
		if (toParam && toParam !== "now") {
			return parseDateFromUrl(toParam);
		}
		return null;
	});
	const [selectedAlert, setSelectedAlert] =
		useState<GroupedNotification | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [alertTypeFilter, setAlertTypeFilter] = useState<string>(
		searchParams.get("action") || "A",
	);
	const [searchFilter, setSearchFilter] = useState<string>("");

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("rollshop-notifications-table-panel-sizes");

	const effectiveEndDate = useMemo(() => {
		return endDate ? endDate.clone().endOf("day").toDate() : moment().toDate();
	}, [endDate]);

	const {
		data: notificationData,
		error: e,
		isError,
		refetch,
	} = useRollshopNotifications({
		shiftId: filterMode === "shift" ? shiftInfo?.CurrentShiftId : null,
		start: filterMode === "period" ? startDate?.toDate() : null,
		end: filterMode === "period" ? effectiveEndDate : null,
		enabled:
			filterMode === "shift" ? !!shiftInfo?.CurrentShiftId : startDate !== null,
	});

	const groupedNotifications = useMemo(() => {
		if (!notificationData) return [];
		return groupNotificationData(notificationData);
	}, [notificationData]);

	const getFilteredNotifications = useMemo(() => {
		return groupedNotifications.filter((notification) => {
			if (alertTypeFilter !== "A") {
				const hasMatchingAlert =
					notification.ActionTypeCode === alertTypeFilter;
				if (!hasMatchingAlert) return false;
			}

			if (searchFilter) {
				const searchLower = searchFilter.toLowerCase();
				const description = notification.Description?.toLowerCase() || "";
				if (!description.includes(searchLower)) return false;
			}

			return true;
		});
	}, [groupedNotifications, alertTypeFilter, searchFilter]);

	const getSelectedNotification = (): GroupedNotification | null => {
		if (!selectedAlert || !groupedNotifications.length) return null;
		return (
			groupedNotifications.find(
				(n) => n.NotificationId === selectedAlert.NotificationId,
			) || null
		);
	};

	const updateUrl = (
		newAction?: string | null,
		newNotificationId?: number | null,
		newMode?: FilterMode,
	) => {
		const mode = newMode ?? filterMode;
		const urlParams = new URLSearchParams();

		const targetNotificationId =
			newNotificationId !== undefined ? newNotificationId : notificationIdParam;
		if (targetNotificationId)
			urlParams.append("notificationId", String(targetNotificationId));

		const targetAction = newAction !== undefined ? newAction : alertTypeFilter;
		if (targetAction && targetAction !== "A")
			urlParams.append("action", targetAction);

		if (mode === "period") {
			if (startDate) urlParams.append("from", formatDateForUrl(startDate));
			if (endDate) {
				urlParams.append("to", formatDateForUrl(endDate));
			} else {
				urlParams.append("to", "now");
			}
			const queryString = urlParams.toString();
			navigate(
				`/${routeRollshopNotifications}${queryString ? `?${queryString}` : ""}`,
			);
		} else {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			const queryString = urlParams.toString();
			navigate(
				`/${routeRollshopNotifications}/${shiftId}${queryString ? `?${queryString}` : ""}`,
			);
		}
	};

	const handleSelectAlert = (
		alert: GroupedNotification | null,
		avoidNavigate?: boolean,
	) => {
		setSelectedAlert(alert);
		if (avoidNavigate) return;

		updateUrl(alertTypeFilter, alert?.NotificationId || null);
	};

	const handleFilterModeChange = (
		_event: React.MouseEvent<HTMLElement>,
		newMode: FilterMode | null,
	) => {
		if (newMode !== null) {
			setFilterMode(newMode);
			updateUrl(alertTypeFilter, null, newMode);
		}
	};

	const handleExportToExcel = () => {
		if (gridApi) {
			const now = new Date();
			const year = now.getFullYear();
			const month = String(now.getMonth() + 1).padStart(2, "0");
			const day = String(now.getDate()).padStart(2, "0");
			const formattedDate = `${year}-${month}-${day}`;

			const columnKeys = gridApi
				.getColumnDefs()
				?.filter(
					(col): col is ColDef =>
						"field" in col && typeof col.field === "string",
				)
				.filter((col) => col.field !== "-")
				.map((col) => col.field)
				.filter((field): field is string => typeof field === "string");

			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: `rollshop_alerts_${formattedDate}.xlsx`,
				columnKeys: columnKeys,
				sheetName: "Rollshop Alerts",
				allColumns: false,
			};

			gridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	useEffect(() => {
		if (filterMode === "shift") {
			if (shiftIdParam) {
				(async () => {
					const shiftId = Number(shiftIdParam);
					await getShiftDataFromAPI(null, shiftId);
				})();
			} else {
				(async () => {
					await getShiftDataFromAPI(null, 0);
				})();
			}
		}
	}, [filterMode]);

	useEffect(() => {
		if (filterMode === "shift" && shiftInfo) {
			updateUrl();
			(async () => {
				await refetch();
			})();
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (filterMode === "period" && startDate !== null) {
			updateUrl();
		}
	}, [startDate, endDate]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<HelmetDexteel title="Rollshop Notifications" />
			<Grid2
				container
				justifyContent="flex-start"
				direction="column"
				p={2}
				spacing={1}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Rollshop Notifications
					</Typography>
				</Grid2>
				<Grid2
					container
					size={{ md: 12 }}
					spacing={2}
					alignItems="center"
					justifyContent="flex-start"
				>
					<Grid2 size={{ xs: 12, md: "auto" }}>
						<Paper
							variant="outlined"
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1.5,
								p: 1,
								minWidth: 660,
							}}
						>
							<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
								<ViewChip
									label="Shift"
									icon={<AccessTime sx={{ fontSize: 16 }} />}
									selected={filterMode === "shift"}
									onClick={() => handleFilterModeChange(null as any, "shift")}
								/>
								<ViewChip
									label="Period"
									icon={<DateRange sx={{ fontSize: 16 }} />}
									selected={filterMode === "period"}
									onClick={() => handleFilterModeChange(null as any, "period")}
								/>
							</Box>
							{filterMode === "shift" ? (
								<Box>
									<ShiftDayNavigatorControl preventInit withPaper={false} />
								</Box>
							) : (
								<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
									<StartFilter value={startDate} onChange={setStartDate} />
									<EndFilter value={endDate} onChange={setEndDate} canBeNull />
								</Box>
							)}
						</Paper>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 2 }}>
						<ActionTypeFilter
							value={alertTypeFilter}
							onChange={(value) => {
								setAlertTypeFilter(value);
								updateUrl(value);
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 3 }}>
						<TextFieldSearchFilter
							value={searchFilter}
							onChange={setSearchFilter}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: "auto" }}>
						<ExcelButton
							handleExportToExcel={handleExportToExcel}
							disabled={getFilteredNotifications.length === 0}
						/>
					</Grid2>
				</Grid2>
				<Grid2 sx={{ width: "100%" }}>
					<Group
						orientation="horizontal"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={20}>
							<TableRollshopNotifications
								rows={getFilteredNotifications}
								setSelectedAlert={handleSelectAlert}
								gridApi={gridApi}
								setGridApi={setGridApi}
								setError={setError}
								setSuccessMessage={setSuccessMessage}
								notificationId={notificationIdParam || undefined}
							/>
						</Panel>
						<CustomSeparator onDoubleClick={handleSeparatorDoubleClick} />
						<Panel id="details" minSize={20}>
							<Paper elevation={2} style={{ height: "100%", width: "100%" }}>
								<NotificationDetails notification={getSelectedNotification()} />
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={successMessage}
				onHide={() => setSuccessMessage("")}
			/>
		</>
	);
};
